import { chartAllColors, dateTypeMap } from '@op/constants';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useFrontWebConfig } from '@op/hooks/useFrontWebConfig';
import { getVideoTagInsight } from '@op/services';
import { aigcGetVideoExtInfo } from '@op/services/aigc';
import { getTimeSpaceArray } from '@op/utils';
import { DBO_T } from '@op/utils/format';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';

// 定义类型
type TSentimentRating = 1 | 3 | 5;
type TSentimentLabel = 'Negative' | 'Neutral' | 'Positive';

const sentimentRatingMap: Record<TSentimentRating, TSentimentLabel> = {
  1: 'Negative',
  3: 'Neutral',
  5: 'Positive',
};
export function getVideoSummary(
  data: any,
  mediaType: 'comment' | 'subtitle' | 'danmaku',
  gameInfo: any,
  keepParams: any,
) {
  const ids = (data?.list || []).map((item: any) => {
    return item.comment_uin;
  });
  if (ids.length === 0) return {};
  return aigcGetVideoExtInfo({
    comment_uins: ids,
    media_type: mediaType,
    start_time: keepParams?.start_time,
    end_time: keepParams?.end_time,
    ...gameInfo,
  }).then((data: any) => {
    const keys = Object.keys(data || {});
    const map: any = {};

    keys.forEach((key: string) => {
      const item = data[key];

      if (item?.summary_status === 'success') {
        map[key] = {
          summary_cn: DBO_T.mdToText(DBO_T.replaceMd(item.summary_cn)),
          summary_en: DBO_T.mdToText(DBO_T.replaceMd(item.summary_en)),
          summary_sentiment:
            sentimentRatingMap[item.sentiment as TSentimentRating] || 'Neutral',
        };
      }
    });
    return map;
  });
}

export function useGptSummaryList({
  data,
  mediaType,
  gameInfo,
  keepParams,
}: {
  data: any;
  mediaType: 'comment' | 'subtitle' | 'danmaku';
  gameInfo: any;
  keepParams: any;
}) {
  const cha_list = useFrontWebConfig({ keys: 'have_video_ai_channel' });
  const channelList = cha_list.map((item: any) => item.channel);
  const [gptSummaryList, setGptSummaryList] = useState<{ [key: string]: any }>(
    {},
  );
  const timerRef = useRef<any>(null);
  const { wrapperRequestTile } = useCancelRequestHook();

  const filter = useMemo(() => {
    const list = (data.list || []).map((item: any) => {
      if (channelList.includes(item?.channel_name)) {
        return item.comment_uin;
      }
    });
    return list.filter((item: any) => Boolean(item));
  }, [data]);

  function clearTimeoutTimer() {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
  }

  function requestVideoSummary(ids: Array<string>) {
    clearTimeoutTimer();

    wrapperRequestTile(aigcGetVideoExtInfo, {
      comment_uins: ids,
      media_type: mediaType,
      start_time: keepParams?.start_time,
      end_time: keepParams?.end_time,
      ...gameInfo,
    })
      .then((data: any) => {
        if (data) {
          setGptSummaryList(pre => ({ ...pre, ...data }));

          const needRequestId = Object.values(data)
            .map((item: any) => {
              if (item?.summary_status === 'init') {
                return item.comment_uin;
              }
            })
            .filter(Boolean);

          if (needRequestId.length > 0) {
            timerRef.current = setTimeout(() => {
              requestVideoSummary(needRequestId);
            }, 60000);
          }
        }
      })
      .catch(error => {
        console.error('Failed to fetch video summary:', error);
      });
  }

  useEffect(() => {
    if (filter.length === 0) return;
    requestVideoSummary(filter);
  }, [filter]);

  useEffect(() => {
    return () => {
      clearTimeoutTimer();
    };
  }, []);

  return {
    gptSummaryList,
  };
}

export function useVideoTagInsight({ filter, extraData }: any) {
  const [chartRecord, setChartRecord] = useState<{
    series?: any[];
    xAxis?: string[];
    legendData?: string[];
    tableData?: any[];
    pieData?: any[];
    pieTotalData?: number;
    isEmpty: boolean;
    list?: {
      label_en: string;
      label_zh: string;
    }[];
    overviewTableData?: {
      [key: string]: string | number;
    }[];
  }>({ isEmpty: true });
  const { loading, wrapperRequestTile } = useCancelRequestHook();

  function requestChart() {
    setChartRecord({ isEmpty: true });
    if (!filter.edition_unified_id) return;
    wrapperRequestTile(getVideoTagInsight, filter).then((data: any) => {
      if (data) {
        const { xAxisKey } = extraData;
        const isTimeAxis = xAxisKey === 'time';
        const { list, timeline_of_start_time, timeline_of_end_time } = data;
        const timeline = data?.timeline?.filter(
          (val: any) => val?.label_en !== 'Others',
        );
        let xAxis: string[] = [];
        const { date_type } = filter;
        const dateType = dateTypeMap[date_type];
        const format =
          dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
        if (isTimeAxis) {
          xAxis = getTimeSpaceArray(
            timeline_of_start_time,
            timeline_of_end_time,
            dateType,
            format,
          );
        } else {
          // 对于 language 和 platform，按照总量从大到小排序
          const uniqueXValues = [
            ...new Set<string>(timeline.map((item: any) => item?.[xAxisKey])),
          ];

          // 计算每个 xAxis 值的总量
          const xAxisTotals = uniqueXValues.map((xVal: string) => {
            const total = timeline
              .filter((item: any) => item?.[xAxisKey] === xVal)
              .reduce((sum: number, item: any) => sum + (item?.value ?? 0), 0);
            return { xVal, total };
          });
          // 按总量从大到小排序
          xAxisTotals.sort((a, b) => b.total - a.total);

          xAxis = xAxisTotals.map((item: any) => item.xVal);
        }

        // Legend 排序逻辑：基于第一个 xAxis 值的数据量排序
        let legendData: string[] = [];
        
        if (xAxis.length > 0) {
          const firstXAxisValue = xAxis[0];
          
          // 获取第一个 xAxis 值对应的所有数据，并按值排序
          const firstXAxisData = timeline
            .filter((item: any) => item?.[xAxisKey] === firstXAxisValue)
            .map((item: any) => ({
              legend: item.label_en,
              value: item.value ?? 0,
            }))
            .sort((a: any, b: any) => b.value - a.value);
          
          const firstXAxisLegends = firstXAxisData.map((item: any) => item.legend);
          
          // 获取所有 legend（去重）
          const allLegends = [...new Set<string>(timeline.map((item: any) => item?.label_en))];
          
          // 找出不在第一个 xAxis 中的 legend，并计算它们的总量用于排序
          const missingLegends = allLegends
            .filter((legend: string) => !firstXAxisLegends.includes(legend) && legend.toLowerCase() !== 'others')
            .map((legend: string) => {
              const total = timeline
                .filter((item: any) => item?.label_en === legend)
                .reduce((sum: number, item: any) => sum + (item?.value ?? 0), 0);
              return { legend, total };
            })
            .sort((a: any, b: any) => b.total - a.total)
            .map((item: any) => item.legend);
          
          // 分离 Others
          const othersLegend = allLegends.find((legend: string) => legend.toLowerCase() === 'others');
          
          // 组合最终顺序：第一个 xAxis 的排序 + 缺失的 legend + Others
          legendData = [
            ...firstXAxisLegends.filter((legend: string) => legend.toLowerCase() !== 'others'),
            ...missingLegends,
            ...(othersLegend ? [othersLegend] : []),
          ];
        } else {
          // 如果没有 xAxis 数据，回退到按总量排序
          const uniqueLegendValues = [
            ...new Set<string>(timeline.map((item: any) => item?.label_en)),
          ];
          
          const legendTotals = uniqueLegendValues.map((legend: string) => {
            const total = timeline
              .filter((item: any) => item?.label_en === legend)
              .reduce((sum: number, item: any) => sum + (item?.value ?? 0), 0);
            return { legend, total };
          });
          
          legendTotals.sort((a: any, b: any) => b.total - a.total);
          
          legendData = legendTotals
            .map((item: any) => item.legend)
            .sort((a: any, b: any) => {
              if (a?.toLocaleLowerCase() === 'others') return 1;
              if (b?.toLocaleLowerCase() === 'others') return -1;
              return 0;
            });
        }

        const series = legendData.map((label, index) => {
          const listData = xAxis.map(xVal => {
            const item = timeline.find(t => {
              if (isTimeAxis) {
                return (
                  t.label_en === label &&
                  moment(t[xAxisKey]).format(format) === xVal
                );
              }
              return t.label_en === label && t[xAxisKey] === xVal;
            });
            return {
              name: xVal,
              value: item?.value ?? 0,
            };
          });

          return {
            key: label,
            data: listData,
            color: chartAllColors[index],
          };
        });

        const pieData = (list || [])
          .filter(val => val.label_en !== 'Others')

          .map((item, index) => ({
            name: item.label_en,
            value: item.value ?? 0,
            itemStyle: {
              color: chartAllColors[index],
            },
            ...item,
          }))
          .sort((a, b) => b.value - a.value);

        const pieTotalData = list.reduce(
          (sum, item) => sum + (item.value ?? 0),
          0,
        );

        const overviewTableData = Object.values(
          timeline.reduce((acc, cur) => {
            const key = cur[xAxisKey]; // e.g. "date"

            if (!acc[key]) {
              acc[key] = { [xAxisKey]: key };
            }

            acc[key][cur.label_en] = cur.value;

            return acc;
          }, {}),
        ).sort((a: any, b: any) => {
          if (xAxisKey === 'time') {
            return (
              moment(b[xAxisKey]).valueOf() - moment(a[xAxisKey]).valueOf()
            );
          }
          return 0;
        }) as { [key: string]: string | number }[];

        const tableData = timeline.sort((a: any, b: any) => {
          if (xAxisKey === 'time') {
            return (
              moment(b[xAxisKey]).valueOf() - moment(a[xAxisKey]).valueOf()
            );
          }
          if (a?.[xAxisKey]?.toLocaleLowerCase() === 'others') return 1;
          if (b?.[xAxisKey]?.toLocaleLowerCase() === 'others') return -1;
          return 0;
        });

        setChartRecord({
          series,
          xAxis,
          legendData,
          tableData,
          isEmpty: timeline?.length === 0,
          pieData,
          pieTotalData,
          list,
          overviewTableData,
        });
      }
    });
  }

  useEffect(() => {
    requestChart();
  }, [filter]);

  return { data: chartRecord, loading, refetch: requestChart };
}
